import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { ArrowRight, BookOpen, Clock, TrendingUp, ArrowLeft, Calendar, User, Tag } from "lucide-react";
import { mockData } from "../data/mock";

const BlogPage = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getColorClasses = (color) => {
    const colorMap = {
      blue: {
        bg: "bg-blue-50",
        text: "text-blue-600",
        border: "border-blue-200",
        icon: "bg-blue-100",
        hover: "hover:bg-blue-100"
      },
      purple: {
        bg: "bg-purple-50", 
        text: "text-purple-600",
        border: "border-purple-200",
        icon: "bg-purple-100",
        hover: "hover:bg-purple-100"
      },
      green: {
        bg: "bg-green-50",
        text: "text-green-600", 
        border: "border-green-200",
        icon: "bg-green-100",
        hover: "hover:bg-green-100"
      },
      teal: {
        bg: "bg-teal-50",
        text: "text-teal-600",
        border: "border-teal-200", 
        icon: "bg-teal-100",
        hover: "hover:bg-teal-100"
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  const handleReadBlog = (post) => {
    navigate(`/blog/${post.id}`);
  };

  // Get all posts for filtering
  const allPosts = mockData.blogCategories.flatMap(category => 
    category.posts.map(post => ({ ...post, category }))
  );

  const filteredPosts = activeCategory === "all" 
    ? allPosts 
    : allPosts.filter(post => post.category.id === activeCategory);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#112b3c]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center">
              <button onClick={() => navigate('/')} className="flex items-center">
                <img 
                  src="/credentra-logo-new.png" 
                  alt="Credentra Logo" 
                  className="h-52 w-auto"
                />
              </button>
            </div>
            <div className="nav-links">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/')}
                className="text-white hover:text-credentra-bright-teal font-medium px-4 py-2 ml-4"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Blog Page Content */}
      <section className="text-white py-20 relative overflow-hidden" style={{background: '#112b3c', paddingTop: '120px'}}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          
          {/* Header */}
          <div className="text-center pt-2 md:pt-1 pb-8 sm:pb-12">
            <div className="hero-badge inline-flex items-center gap-2 mb-8 sm:mb-12" style={{background: '#112b3c'}}>
              <BookOpen className="h-4 w-4" />
              <span className="leading-tight">
                Knowledge Hub
              </span>
            </div>
            
            <div className="hero-content">
              <h1 className="hero-title mb-6">
                <span>Insights & Resources</span>
              </h1>
              
              <p className="hero-subtitle mb-8">
                From FICA tip credits to technical AI insights — explore content for restaurant owners and CPAs
              </p>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-credentra-bright-teal/10 rounded-full blur-3xl animate-pulse" style={{animationDelay: '1s'}}></div>
        </div>
      </section>

      {/* Blog Content Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Category Navigation */}
          <div className="mb-12">
            <div className="text-center mb-12">
              <div className="section-badge text-blue-600 bg-blue-50">
                <Tag className="h-3 w-3" />
                Categories
              </div>
              <h2 className="enterprise-heading text-3xl md:text-4xl font-bold text-credentra-dark-teal mb-6">
                Browse by Topic
              </h2>
              <p className="enterprise-subheading text-lg max-w-2xl mx-auto text-gray-600">
                Find exactly what you're looking for with our organized content categories
              </p>
            </div>

            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <Button
                variant={activeCategory === "all" ? "default" : "outline"}
                className={`text-sm md:text-base px-4 md:px-6 py-3 rounded-full font-medium transition-all duration-300 ${activeCategory === "all" 
                  ? "text-white shadow-lg hover:scale-105" 
                  : "border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-credentra-dark-teal hover:text-credentra-dark-teal"
                }`}
                style={activeCategory === "all" ? {background: 'linear-gradient(135deg, #00d6cf, #3c778d)'} : {}}
                onClick={() => setActiveCategory("all")}
              >
                All Posts ({allPosts.length})
              </Button>
              {mockData.blogCategories.map((category) => {
                const colors = getColorClasses(category.color);
                const postCount = category.posts.length;
                return (
                  <Button
                    key={category.id}
                    variant={activeCategory === category.id ? "default" : "outline"}
                    className={`text-sm md:text-base px-4 md:px-6 py-3 rounded-full font-medium transition-all duration-300 ${activeCategory === category.id 
                      ? `text-white shadow-lg hover:scale-105` 
                      : `border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-credentra-dark-teal hover:text-credentra-dark-teal`
                    }`}
                    style={activeCategory === category.id ? {background: 'linear-gradient(135deg, #00d6cf, #3c778d)'} : {}}
                    onClick={() => setActiveCategory(category.id)}
                  >
                    <category.icon className="h-3 w-3 md:h-4 md:w-4 mr-2" />
                    <span className="hidden sm:inline">{category.title}</span>
                    <span className="sm:hidden">{category.title.split(' ')[0]}</span>
                    <span className="ml-1">({postCount})</span>
                  </Button>
                );
              })}
            </div>

            {/* Category Description */}
            {activeCategory !== "all" && (
              <div className="text-center mb-8">
                {(() => {
                  const category = mockData.blogCategories.find(c => c.id === activeCategory);
                  if (!category) return null;
                  const colors = getColorClasses(category.color);
                  return (
                    <div className={`inline-flex items-center gap-3 ${colors.bg} ${colors.border} border rounded-xl px-6 py-3`}>
                      <div className={`w-8 h-8 ${colors.icon} rounded-lg flex items-center justify-center`}>
                        <category.icon className={`h-4 w-4 ${colors.text}`} />
                      </div>
                      <div className="text-left">
                        <div className={`font-semibold ${colors.text}`}>{category.title}</div>
                        <div className="text-sm text-gray-600">{category.description}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Blog Posts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-12">
            {filteredPosts.map((post) => {
              const colors = getColorClasses(post.category.color);
              return (
                <div key={post.id} className="enterprise-card group cursor-pointer relative transition-all duration-300 hover:shadow-xl flex flex-col min-h-[500px] p-6" 
                     onClick={() => handleReadBlog(post)}>
                  
                  {/* Category Badge - Fixed positioning */}
                  <div className="absolute top-4 right-4 z-10">
                    <div className={`${colors.bg} ${colors.border} border rounded-lg px-3 py-1.5 shadow-sm`}>
                      <div className="flex items-center gap-1.5">
                        <post.category.icon className={`h-3 w-3 ${colors.text}`} />
                        <span className={`text-xs font-medium ${colors.text}`}>{post.category.title}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4 pr-20">
                    <div className={`w-12 h-12 md:w-14 md:h-14 ${colors.icon} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <post.category.icon className={`h-5 w-5 md:h-7 md:w-7 ${colors.text}`} />
                    </div>
                    
                    {/* Meta Information */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500 mb-3">
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{post.readTime} min read</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(post.date).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content Section - Takes up available space */}
                  <div className="flex-grow">
                    <h3 className="text-lg md:text-xl font-bold text-credentra-dark-teal mb-3 line-clamp-2 transition-colors leading-tight" 
                        style={{color: 'rgb(31, 71, 97)'}} 
                        onMouseEnter={(e) => e.target.style.color = '#112b3c'} 
                        onMouseLeave={(e) => e.target.style.color = 'rgb(31, 71, 97)'}>
                      {post.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-3 text-sm md:text-base leading-relaxed">
                      {post.excerpt}
                    </p>
                  </div>

                  {/* Footer Section - Always at bottom */}
                  <div className="mt-auto">
                    {/* Tags */}
                    {post.tags && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, index) => (
                          <Badge key={index} variant="secondary" className="text-xs bg-gray-100 text-gray-700 hover:bg-gray-200 px-2 py-1">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                    
                    {/* Author & CTA */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <User className="h-4 w-4" />
                        <span className="truncate">{post.author}</span>
                      </div>
                      <div className="flex items-center font-semibold group-hover:gap-3 transition-all duration-300 flex-shrink-0" style={{color: '#112b3c'}}>
                        <span className="text-sm">Read More</span>
                        <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-16">
              <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No posts in this category yet</h3>
              <p className="text-gray-500 mb-6">We're working on creating great content for this section.</p>
              <Button variant="outline" onClick={() => setActiveCategory("all")}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                View All Posts
              </Button>
            </div>
          )}

          {/* Coming Soon Categories */}
          {mockData.blogCategories.filter(category => category.posts.length === 0).length > 0 && (
            <div className="border-t border-gray-200 pt-12">
              <h3 className="text-2xl font-bold text-credentra-dark-teal text-center mb-8">Coming Soon</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {mockData.blogCategories
                  .filter(category => category.posts.length === 0)
                  .map((category) => {
                    const colors = getColorClasses(category.color);
                    return (
                      <div key={category.id} className={`${colors.bg} ${colors.border} border rounded-xl p-6 text-center`}>
                        <div className={`w-12 h-12 ${colors.icon} rounded-xl flex items-center justify-center mx-auto mb-4`}>
                          <category.icon className={`h-6 w-6 ${colors.text}`} />
                        </div>
                        <h4 className={`font-semibold ${colors.text} mb-2`}>{category.title}</h4>
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default BlogPage;